import { useState, useMemo, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Button, Card, Input, cn, Avatar, AvatarFallback, AvatarImage, Badge } from '@pantry/ui';
import { useAdminUsers, type AdminUserQueryParams, type AdminUser } from '@pantry/api-client';
import { APP_ROLES, type AppRole } from '@pantry/shared';
import {
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UsersIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { UserDetailsSheet } from './user-details-sheet';

function getRoleLabels(t: ReturnType<typeof useLingui>['t']): Record<AppRole, string> {
  return {
    superadmin: t`Super Admin`,
    admin: t`Admin`,
    user: t`User`,
  };
}

const ROLE_COLORS: Record<AppRole, string> = {
  superadmin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  admin: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  user: 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

interface UserRowProps {
  user: AdminUser;
  onSelect: (user: AdminUser) => void;
}

function UserRow({ user, onSelect }: UserRowProps) {
  const { t, i18n } = useLingui();
  const ROLE_LABELS = getRoleLabels(t);
  const initials = user.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user.email[0].toUpperCase();

  return (
    <tr
      className="group border-b border-border/50 last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
      onClick={() => onSelect(user)}
    >
      {/* User info */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <Avatar className="h-9 w-9">
            {user.avatarUrl && <AvatarImage src={user.avatarUrl} alt={user.name || user.email} />}
            <AvatarFallback className="text-sm font-medium">{initials}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-foreground truncate">
                {user.name || t`No name`}
              </span>
              {user.isRestricted && (
                <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
              )}
            </div>
            <span className="text-sm text-muted-foreground truncate block">{user.email}</span>
          </div>
        </div>
      </td>

      {/* Role */}
      <td className="px-4 py-3">
        <Badge variant="secondary" className={cn('font-medium', ROLE_COLORS[user.appRole])}>
          {ROLE_LABELS[user.appRole]}
        </Badge>
      </td>

      {/* Joined date */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden sm:table-cell">
        {new Date(user.createdAt).toLocaleDateString(i18n.locale, {
          month: 'short',
          year: 'numeric',
        })}
      </td>

      {/* Last active */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">
        {user.lastLoginAt
          ? formatRelativeTime(new Date(user.lastLoginAt), t, i18n.locale)
          : t`Never`}
      </td>

      {/* Households */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
        {user.householdCount}
      </td>

      {/* Items */}
      <td className="px-4 py-3 text-sm text-muted-foreground hidden lg:table-cell">
        {user.itemCount.toLocaleString(i18n.locale)}
      </td>
    </tr>
  );
}

function formatRelativeTime(
  date: Date,
  t: ReturnType<typeof useLingui>['t'],
  locale: string
): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return t`Just now`;
  if (diffMins < 60) return t`${diffMins}m ago`;
  if (diffHours < 24) return t`${diffHours}h ago`;
  // i18n: "d" abbreviates "days", relative time
  if (diffDays < 7) return t`${diffDays}d ago`;
  if (diffDays < 30) return t`${Math.floor(diffDays / 7)}w ago`;
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

type FilterOption = AppRole | 'all' | 'restricted';

function getFilterOptions(
  t: ReturnType<typeof useLingui>['t']
): { value: FilterOption; label: string; icon?: React.ReactNode }[] {
  return [
    { value: 'all', label: t`All Users` },
    {
      value: 'superadmin',
      label: t`Super Admins`,
      icon: <ShieldCheckIcon className="h-3.5 w-3.5" />,
    },
    { value: 'admin', label: t`Admins`, icon: <ShieldExclamationIcon className="h-3.5 w-3.5" /> },
    { value: 'user', label: t`Users`, icon: <UsersIcon className="h-3.5 w-3.5" /> },
    {
      value: 'restricted',
      label: t`Restricted`,
      icon: <ExclamationTriangleIcon className="h-3.5 w-3.5" />,
    },
  ];
}

export function AdminUsers() {
  const { t, i18n } = useLingui();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterOption>('all');
  const [page, setPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const limit = 25;
  const ROLE_LABELS = getRoleLabels(t);
  const FILTER_OPTIONS = getFilterOptions(t);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to first page on search
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Build query params
  const queryParams: AdminUserQueryParams = useMemo(() => {
    const params: AdminUserQueryParams = {
      page,
      limit,
      sortBy: 'createdAt',
      sortOrder: 'desc',
    };

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    if (activeFilter === 'restricted') {
      params.status = 'restricted';
    } else if (activeFilter !== 'all' && APP_ROLES.includes(activeFilter as AppRole)) {
      params.role = activeFilter as AppRole;
    }

    return params;
  }, [page, limit, debouncedSearch, activeFilter]);

  const { data, isLoading, error } = useAdminUsers(queryParams);

  const handleFilterChange = (filter: FilterOption) => {
    setActiveFilter(filter);
    setPage(1); // Reset to first page on filter change
  };

  const totalPages = data?.pagination.totalPages || 1;
  const total = data?.pagination.total || 0;
  const startItem = (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
          <UsersIcon className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-semibold text-foreground">
            <Trans>Users</Trans>
          </h1>
          <p className="text-muted-foreground">
            <Trans>Manage user accounts and roles</Trans>
          </p>
        </div>
      </div>

      {/* Search and filters */}
      <Card className="rounded-xl">
        <div className="p-4 space-y-4">
          {/* Search input */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t`Search users by name or email...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>

          {/* Filter pills */}
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                onClick={() => handleFilterChange(option.value)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                  activeFilter === option.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                )}
              >
                {option.icon}
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {/* User table */}
      <Card className="rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-4 text-destructive">
              Failed to load users: {error.message}
            </div>
          </div>
        ) : data?.data.length === 0 ? (
          <div className="p-12 text-center">
            <UsersIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-3" />
            <p className="text-muted-foreground">
              {debouncedSearch
                ? t`No users found matching "${debouncedSearch}"`
                : t`No users found`}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>User</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                      <Trans>Role</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden sm:table-cell">
                      <Trans>Joined</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden md:table-cell">
                      <Trans>Last Active</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">
                      <Trans>Households</Trans>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground hidden lg:table-cell">
                      <Trans>Items</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((user) => (
                    <UserRow key={user.id} user={user} onSelect={setSelectedUser} />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
              <p className="text-sm text-muted-foreground">
                <Trans>
                  Showing {startItem}-{endItem} of {total.toLocaleString(i18n.locale)} users
                </Trans>
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="rounded-lg"
                >
                  <ChevronLeftIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Previous</Trans>
                  </span>
                </Button>
                <span className="text-sm text-muted-foreground">
                  <Trans>
                    Page {page} of {totalPages}
                  </Trans>
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg"
                >
                  <ChevronRightIcon className="h-4 w-4" />
                  <span className="sr-only">
                    <Trans>Next</Trans>
                  </span>
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* User details side sheet */}
      {selectedUser && (
        <UserDetailsSheet user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}
